import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { required, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import moment from "moment";

import { PermissionManager } from "../../../../../../common/utilities/permission-manager";
import { NavigationService } from "../../../../../../services/navigation.service";
import { ApplicationEnum } from "../../../../../../enums/ApplicationEnum";
import { formatDate, formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import config from "../../../../../../common/config";
import { HopeFilters } from "../../../../../../enums/HopeFiltersEnum";
import { HuvSfvTaskStatus } from "../../../../../../enums/HuvSfvTaskStatusEnum";
import { TaskRepeat } from "../../../../../../enums/TaskRepeatEnum";
import { enumToMap } from "../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { IDateRangePickerOptions } from "../../../../../../resources-vue/vue-interfaces/i-date-range-picker";
import {
    IAvailableTaskItems,
    IPayersByDateResponseData
} from "../../../../../../resources-vue/vue-interfaces/i-patient-task-hos-pal";
import { IEmployeeList } from "../../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { PatientTask } from "../../../../../../resources-vue/vue-models/patient-task-hos-pal";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { PatientService } from "../../../../../../services/patient.service";
import { TaskService } from "../../../../../../services/task.service";
import { PatientStatus, PayerTypes, PayerTypesEnum } from "../../../../../../enums/enums";
import { ToastrService } from "../../../../../../services/toastr.service";
import { HttpStatusCodeEnum } from "../../../../../../common/http-status-code-enum";
import { AnalyticsCategory } from "../../../../../../services/analytics.service";
import { getHospiceRedirectionUrl } from "../../../../../../common/get-hospice-redirection-url";

import FrequencyCheck from "../../../../../../resources-vue/vue-elements/FrequencyCheck/FrequencyCheck.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TimePicker from "../../../../../../resources-vue/vue-custom-elements/TimePicker/TimePicker.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import DateRangePicker from "../../../../../../resources-vue/vue-custom-elements/DateRangePicker/DateRangePicker.vue";
import MultiDatePicker from "../../../../../../resources-vue/vue-custom-elements/MultiDatePicker/MultiDatePicker.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import EmptyTextHandler from "../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export enum SaveEnum {
    SaveAndExit = "1",
    SaveAndAddAnother = "2"
}

export default defineComponent({
    components: {
        EmptyTextHandler,
        FormSection,
        DatePicker,
        DateRangePicker,
        MultiDatePicker,
        TypeaheadInput,
        TimePicker,
        FrequencyCheck
    },
    props: {
        columns: {
            type: Number
        },
        statusItem: {
            type: Object
        },

        application: {
            type: Number
        },
        selectedAgency: {
            type: String
        },
        pageReset: {
            type: Function
        }
    },
    data(vm) {
        return {
            _toastrService: null as ToastrService,
            _taskService: null as TaskService,
            _patientService: null as PatientService,
            _permissionManager: null as PermissionManager,
            _navigationService: null as NavigationService,
            hopeFilters: HopeFilters,
            taskStatuses: HuvSfvTaskStatus,
            isEditRequested: false as boolean,
            isLoading: false as boolean,
            newTaskRepeat: 0 as number,
            tasks: TaskRepeat,
            newTaskSingleDate: "" as string,
            newTaskDateRange: "" as string,
            newTaskFlexDates: "" as string,
            newTaskRangePickerOptions: {
                dateLimit: {
                    days: 90
                }
            } as IDateRangePickerOptions,
            availableTask: null as IAvailableTaskItems[],
            taskList: {} as IAvailableTaskItems,
            isDateSelected: false,
            isTaskSelected: false,
            employeeList: [] as IEmployeeList[],
            scheduleEmployeeId: {} as ITypeaheadOptions,
            newTask: new PatientTask() as PatientTask,
            payerTypes: [] as IEnumResponse[],
            patientPayers: [] as IPayersByDateResponseData[],
            shiftLength: 0 as number,
            patient: null as any,
            isPatientPending: false,
            payerTypeTest: Array.from(PayerTypes.enumWithDescriptions.values()),
            taskListItems: null as IAvailableTaskItems,
            employeeName: null as string,
            isOutsideFrequency: false,
            shiftStartTime: null as string,
            shiftEndTime: null as string,
            visitDates: [],
            isFrequencyWarningRequested: false as boolean,
            saveType: null as string,
            v$: useVuelidate(),
            formatDate,
            enumToMap,
            formatDateInput
        };
    },
    validations() {
        return {
            newTaskSingleDate: {
                required: requiredIf(() => {
                    return this.newTaskRepeat == 0;
                })
            },
            newTaskDateRange: {
                required: requiredIf(() => {
                    return this.newTaskRepeat == 1 || this.newTaskRepeat == 2 || this.newTaskRepeat == 4;
                })
            },
            newTaskFlexDates: {
                required: requiredIf(() => {
                    return this.newTaskRepeat == 3;
                })
            },
            taskList: { required },
            newTask: {
                patientPayerId: { required }
            },
            shiftEndTime: {
                required: requiredIf(() => {
                    return !!this.shiftStartTime;
                })
            }
        };
    },
    created() {
        ((this._toastrService = Container.instance.get(ToastrService)),
            (this._taskService = Container.instance.get(TaskService)));
        this._permissionManager = Container.instance.get(PermissionManager);
        this._navigationService = Container.instance.get(NavigationService);
        this._patientService = Container.instance.get(PatientService);
    },
    computed: {
        getHuvSfvName() {
            return this.hopeFilters.getDescriptionUsingValue(this.statusItem.TaskType);
        },
        getTaskStatusName() {
            return this.taskStatuses.getDescriptionUsingValue(this.statusItem.TaskStatus);
        },
        getDaysRemaining() {
            if (!this.statusItem.DaysRemaining) {
                return null;
            }
            return this.statusItem.DaysRemaining < 0
                ? `${Math.abs(this.statusItem.DaysRemaining)} day(s) overdue`
                : `${this.statusItem.DaysRemaining} day(s) remaining`;
        },
        newTaskStartDates() {
            return this.newTask?.startDates?.filter((startDate) => moment(startDate, "MM/DD/YYYY", true).isValid());
        },
        getVisitDetails() {
            return {
                ...this.statusItem,
                Id: this.statusItem.PatientId,
                AgencyId: this.selectedAgency.split("_")[0],
                Application: this.application
            };
        },
        getStatusName() {
            const status = PatientStatus.getDescriptionUsingValue(this.statusItem.PatientStatus);
            const firstLetter = status.charAt(0).toUpperCase();
            const rest = status.slice(1);
            return firstLetter + rest;
        }
    },

    watch: {
        newTaskRepeat() {
            this.newTaskSingleDate = "";
            this.newTaskDateRange = "";
            this.newTaskFlexDates = "";
            this.newTask.startDates = [];
            this.newTask.endDates = [];
        },
        newTaskSingleDate(newValue: string) {
            this.newTask.startDates = [];
            if (!!newValue) {
                this.newTask.startDates.push(newValue);
            }
            if (this.taskDateChanged) {
                this.taskDateChanged();
            }
        },
        newTaskDateRange(newValue: string) {
            this.newTask.startDates = [];
            const newTaskDates = newValue.split(" - ");
            const startDate = moment(newTaskDates[0]);
            const endDate = moment(newTaskDates[1]);
            let dateToAdd = startDate;
            while (dateToAdd <= endDate) {
                this.newTask.startDates.push(dateToAdd.format("MM/DD/YYYY"));
                if (this.newTaskRepeat == 4) {
                    dateToAdd = dateToAdd.add(1, "month");
                } else {
                    dateToAdd = dateToAdd.add(this.newTaskRepeat, "week");
                }
            }
            if (this.taskDateChanged) {
                this.taskDateChanged();
            }
        },
        newTaskFlexDates(newValue: string) {
            this.newTask.startDates = [];
            if (!!newValue) {
                this.newTask.startDates = newValue.split(", ");
            }
            if (this.taskDateChanged) {
                this.taskDateChanged();
            }
        },
        taskList(newValue: IAvailableTaskItems) {
            if (newValue) {
                this.newTask.taskId = newValue.id;
                this.taskListItems = newValue;
                const response = this.getEmployeeList("");
                response.then(() => {
                    this.isTaskSelected = true;
                });
                this.updateAvailablePayors();
            }
        },
        scheduleEmployeeId(newValue: ITypeaheadOptions) {
            if (newValue) {
                this.newTask.userId = newValue.value;
                this.employeeName = newValue.name;
            }
        },
        shiftStartTime() {
            if (this.shiftLength > 0 && this.newTask.startDates && this.newTask.startDates.length > 0) {
                this.newTask.startDates = this.newTask.startDates.map((item) => {
                    const startDate = new Date(`${item} ${this.shiftStartTime}`).toISOString();
                    return moment(startDate).format("MM/DD/YYYY HH:mm");
                });
                this.newTask.endDates = [];
                this.shiftEndTime = moment(this.shiftStartTime, "hh:mm A")
                    .add(this.shiftLength, "hours")
                    .format("hh:mm A");
                this.newTask.startDates.forEach((item) => {
                    const endDate = moment(item).add(this.shiftLength, "hours").format("MM/DD/YYYY HH:mm");
                    this.newTask.endDates.push(endDate);
                });
            }
        },
        shiftEndTime() {
            if (!this.shiftEndTime) {
                this.newTask.endDates = this.newTask.endDates.map((endDate) => moment(endDate).format("MM/DD/YYYY"));
                return;
            }
            let scheduledDates = this.newTask.startDates.map((startDate) => moment(startDate).format("MM/DD/YYYY"));
            if (scheduledDates && scheduledDates.length > 0) {
                this.newTask.endDates = [];
                this.newTask.endDates = scheduledDates.map((item) => {
                    let startDate = new Date(`${item} ${this.shiftEndTime}`).toISOString();
                    return moment(startDate).format("MM/DD/YYYY HH:mm");
                });
            }
        }
    },

    methods: {
        async handlePatientNameClick(resource: any) {
            let redirectResponse;
            let action = "AxxessHospiceFE";
            redirectResponse = {
                redirectUrl: `${getHospiceRedirectionUrl(this.selectedAgency.split("_")[0])}/patients/patient-chart/${resource.PatientId}?accountId=${this.selectedAgency.split("_")[0]}`,
                isSuccessful: true
            };
            action = "AxxessHospice";
            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },
        addTask(patientId: any) {
            this.isEditRequested = true;
            this.getCurrentBenefitPeriod(patientId);
        },
        changeNewTaskSingleDate(date: string, name: string) {
            if (name == "newTaskSingleDate") {
                this.newTaskSingleDate = date;
            }
        },
        setNewTaskDateRange(event: any) {
            this.newTaskDateRange = event.target.value;
        },
        updatenewTaskFlexDates(result: any) {
            this.newTaskFlexDates = result;
        },
        availableTasksFilter(filter: string) {
            if (filter) {
                const filterAvailableTasks = this.availableTask.filter((task) => {
                    return task.name.toLowerCase().includes(filter.toLowerCase());
                });
                return filterAvailableTasks;
            } else {
                return this.availableTask;
            }
        },
        changeTaskList(data: any) {
            this.taskList = data;
        },
        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.employeeList.filter((employee) => {
                    const fullName = `${employee.firstName} ${employee.lastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item) => {
                    return {
                        name: `${item.firstName} ${item.lastName}`,
                        value: item.id
                    };
                });
            } else {
                return this.employeeList?.map((item) => ({
                    value: item.id,
                    name: `${item.firstName} ${item.lastName}`
                }));
            }
        },
        changeScheduleEmployeeId(data: any) {
            this.scheduleEmployeeId = data;
        },
        changeStartTime(time: any) {
            this.shiftStartTime = time;
        },
        cancel() {
            this.isEditRequested = false;
            this.newTask = new PatientTask();
            this.newTaskRepeat = 0;
            this.newTaskSingleDate = "";
            this.newTaskDateRange = "";
            this.newTaskFlexDates = "";
            this.shiftLength = 0;
            this.shiftStartTime = "";
            this.scheduleEmployeeId = null;
            this.taskList = null;
        },
        async getCurrentBenefitPeriod(PatientId: any) {
            const response = await this._patientService.getCurrentBenefitPeriod({
                patientId: PatientId,
                agencyId: this.selectedAgency.split("_")[0],
                application: ApplicationEnum.AxxessHospiceFE
            });
            this.patient = response.data;
        },
        taskDateChanged() {
            if (this.newTask.startDates?.length > 0) {
                const response = this.availableTasks();
                response.then(() => {
                    this.isDateSelected = true;
                });
            } else {
                this.isDateSelected = false;
                this.resetShiftOnCall();
            }
            this.updateAvailablePayors();
        },
        async availableTasks() {
            if (this.newTask.startDates.length) {
                const data = await this._taskService.getAvailableTasks({
                    patientId: this.statusItem.PatientId,
                    agencyId: this.selectedAgency.split("_")[0],
                    application: ApplicationEnum.AxxessHospiceFE,
                    startDates: this.newTask.startDates
                });
                return (this.availableTask = data.Data);
            } else {
                return false;
            }
        },
        resetShiftOnCall() {
            this.newTask.onCall = false;
            this.shiftStartTime = "";
            this.shiftLength = 0;
        },
        async updateAvailablePayors() {
            if (!this.newTask.startDates.length) {
                this.patientPayers = [];
                return;
            }
            const startDates = this.newTask.startDates.map((date) => moment(date));
            const startDate = moment.min(startDates).format("MM/DD/YYYY");
            const endDate = moment.max(startDates).format("MM/DD/YYYY");
            const response = await this._taskService.getPayersByDate({
                patientId: this.statusItem.PatientId,
                accountId: this.selectedAgency.split("_")[0],
                startDate: startDate,
                endDate: endDate,
                application: ApplicationEnum.AxxessHospiceFE,
                includeCharityPayer: true
            });
            this.patientPayers = response.data;

            this.payerTypes = this.payerTypeTest.filter((type) =>
                this.patientPayers.some((patientPayor) => patientPayor.payerType === type.value)
            );

            const primaryPayer = this.patientPayers.find((payer) => payer.payerType === PayerTypesEnum.Primary);

            this.newTask.patientPayerId = primaryPayer?.id ?? null;
        },
        async getEmployeeList(filter: string) {
            const employees = await this._taskService.getAvailableUsers({
                term: filter,
                accountId: this.selectedAgency.split("_")[0],
                application: ApplicationEnum.AxxessHospiceFE,
                patientId: this.statusItem.PatientId,
                taskId: this.newTask.taskId
            });
            return (this.employeeList = employees);
        },
        async createTask(saveType: string) {
            const validated = await this.v$.$validate();
            if (!validated) {
                return;
            }
            if (!!this.newTask) {
                try {
                    this.isLoading = true;
                    this.$emit("update:isSaving", true);
                    this.newTask.patientId = this.statusItem.PatientId;
                    this.newTask.metaId = "";
                    this.newTask.comments = "";
                    this.newTask.agencyId = this.selectedAgency.split("_")[0];
                    this.newTask.application = ApplicationEnum.AxxessHospiceFE;
                    await this._taskService.createTask(this.newTask);
                    this._toastrService.success({
                        title: `New Task Created`,
                        message: `The Task has been created.`
                    });
                    this.closeFrequencyWarning();
                    if (saveType == SaveEnum.SaveAndExit) {
                        // trigger callback
                        this.isEditRequested = false;
                    } else {
                        (this.$refs.typeAheadRefTask as any)?.clearFilter();
                        (this.$refs.typeAheadRefEmp as any)?.clearFilter();
                        setTimeout(() => {
                            this.newTask = new PatientTask();
                            this.newTaskSingleDate = "";
                            this.newTaskDateRange = "";
                            this.newTaskFlexDates = "";
                            this.scheduleEmployeeId = null;
                            this.taskList = null;
                        });
                    }
                } catch (e) {
                    // For 400 bad request, FE have app level catch to display error msg which FE recieved from BE
                    if (e.status !== HttpStatusCodeEnum.BadRequest) {
                        this._toastrService.error({
                            title: `Creating Task Failed`,
                            message: `Creating Task Operation Failed, Please Contact Axxess if issue still persists.`
                        });
                    }
                } finally {
                    this.$emit("update:isSaving", false);
                    this.isLoading = false;
                    this.pageReset();
                    this.cancel();
                }
            }
        },
        updateVisitDates(dates: any) {
            this.visitDates = dates;
        },
        updateOutsideFrequency(frequency: any) {
            this.isOutsideFrequency = frequency;
        },
        async showFrequencyWarning(saveTask: boolean = true) {
            const validated = await this.v$.$validate();
            if (!validated) {
                return;
            }
            this.isFrequencyWarningRequested = true;
            if (saveTask == true) {
                this.saveType = SaveEnum.SaveAndAddAnother;
            } else {
                this.saveType = SaveEnum.SaveAndExit;
            }
        },
        closeFrequencyWarning() {
            this.isOutsideFrequency = false;
            this.isFrequencyWarningRequested = false;
        },
        async saveAndAddAnother(saveType: string) {
            await this.createTask(saveType);
        }
    }
});
